import { IAdherenceRepository } from '../interfaces';
import LogUtil from '../../../core/utils/LogUtil';

/**
 * Adherence Repository — 운영 RDS의 백엔드 endpoint를 호출하고
 * 응답을 AdherencePage가 기대하는 형태로 매핑한다.
 *
 * 매핑 헬퍼:
 *   - patient (long/short) 행 → name/adherence/weekly/frequency/day_label/...
 *   - detail (groups[]) → prescription/dose_kpi/weekly_calendar/message_history
 */

const ok = (raw) => ({
  result_code: raw?.result_code ?? '200',
  resultCode: raw?.resultCode ?? 200,
  data: raw?.data ?? raw,
});

// ─── 백엔드 status (한글) → 프론트 STATUS_TAG 키 ──────────────────
// 장기 5단계 (정책서: S1~S5)
const STATUS_MAP_LONG = {
  '정상':     'normal',     // S1
  '저조':     'poor',       // S2
  '중단위험': 'churn_risk', // S3
  '종료임박': 'expiring',   // S4
  '이탈':     'churned',    // S5
};
// 단기 4단계
const STATUS_MAP_SHORT = {
  '복용중':     'active',
  '복용불안정': 'unstable',
  '종료임박':   'expiring',
  '복용완료':   'completed',
};

// ─── recent_records.status → weekly dot ──────────────────────
const RECORD_TO_DOT = {
  complete: 'good',
  partial: 'warning',
  missed: 'miss',
  no_prescription: 'empty',
};

// ─── 헬퍼 ─────────────────────────────────────────────────────
const normalizeGender = (g) => {
  if (!g) return null;
  const u = String(g).trim().toUpperCase();
  if (u === 'M' || u === 'MALE' || u === '남' || u === '남성') return 'M';
  if (u === 'F' || u === 'FEMALE' || u === '여' || u === '여성') return 'F';
  return g;
};

const formatMD = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return '';
  const parts = yyyy_mm_dd.split('-');
  if (parts.length < 3) return yyyy_mm_dd;
  return `${parseInt(parts[1], 10)}/${parseInt(parts[2], 10)}`;
};

// 값 없으면 null — 화면에서 '-' 처리 (가짜 기본값 금지)
const roundOrNull = (v) => (v == null ? null : Math.round(v));
const freqLabel = (n) => (n ? `1일 ${n}회` : null);

export const mapPatientLong = (p) => ({
  user_id: p.user_id,
  name: p.patient_name,
  gender: normalizeGender(p.gender),
  age: p.age,
  adherence: roundOrNull(p.compliance_rate),
  weekly: (p.recent_records || []).map(r => RECORD_TO_DOT[r.status] ?? 'empty'),
  frequency: freqLabel(p.frequency_per_day),
  day_label: p.elapsed_days != null ? `${p.elapsed_days}일차` : null,
  period_label: p.total_days != null ? `${p.total_days}일` : null,
  status: STATUS_MAP_LONG[p.status] || null,
  challenge_in_progress: !!p.challenge_in_progress,
  // 병행 단기처방 (C-1) — 없으면 null
  short_term: p.short_term ? {
    ...p.short_term,
    status: STATUS_MAP_SHORT[p.short_term.status] || null,
  } : null,
});

export const mapPatientShort = (p) => ({
  user_id: p.user_id,
  name: p.patient_name,
  gender: normalizeGender(p.gender),
  age: p.age,
  adherence: roundOrNull(p.compliance_rate),
  weekly: (p.recent_records || []).map(r => RECORD_TO_DOT[r.status] ?? 'empty'),
  frequency: freqLabel(p.frequency_per_day),
  dose_label: p.taken_doses != null && p.total_doses != null ? `${p.taken_doses}/${p.total_doses}회` : null,
  remaining_label: p.remaining_days != null ? `${p.remaining_days}일` : null,
  status: STATUS_MAP_SHORT[p.status] || null,
  challenge_in_progress: !!p.challenge_in_progress,
});

export const mapDetail = (d) => {
  const groups = d.groups || [];
  // 서버가 type=long|short으로 필터링했을 경우 groups[0]이 해당 타입.
  // type 미지정(하위 호환) 시 장기 우선, 없으면 첫 번째.
  const long = groups.find(g => g.is_long_term) || groups[0] || null;
  const meds = (long?.medications || []).map(m => ({
    name: `${m.medication_name || m.name}${m.dosage_per_dose ? ` ${m.dosage_per_dose}` : ''}`,
    // "1일 2회 - 아침/저녁" (dose_times)
    schedule: [freqLabel(m.frequency_per_day), m.dose_times?.length ? m.dose_times.join('/') : null]
      .filter(Boolean).join(' - ') || null,
  }));

  const dk = long?.dose_kpi || {};

  const baseStatus = long?.is_long_term
    ? STATUS_MAP_LONG[long?.status]
    : STATUS_MAP_SHORT[long?.status];

  const freq = freqLabel(dk.freq_per_day);
  const days = long?.total_days != null ? `${long.total_days}일` : null;

  return {
    user_id: d.user_id,
    name: d.patient_name,
    gender: normalizeGender(d.gender),
    age: d.age,
    phone_number: d.phone_number,
    last_visit: d.last_visit,
    status: baseStatus || null,
    allergy_info: d.allergy_info || null,
    adherence_percent: roundOrNull(long?.compliance_rate),
    prescription: long ? {
      diagnosis: long.disease_name
        ? `${long.disease_name}${long.disease_code ? ` (${long.disease_code})` : ''}`
        : null,
      atc_code: (long.atc_codes?.length ? long.atc_codes.join(', ') : long.atc_code) || null,
      dosing: [freq, days].filter(Boolean).join(' / ') || null,
      hospital_name: long.hospital_name || null,
      medications: meds,
    } : null,
    dose_kpi: long ? [
      { label: '총 복용횟수',  value: dk.total_taken_count,      unit: '회', primary: true },
      { label: '누락 횟수',    value: dk.missed_count,           unit: '회' },
      { label: '잔여 횟수',    value: dk.remaining_count,        unit: '회' },
      { label: '실 잔여 횟수', value: dk.actual_remaining_count, unit: '회' },
    ] : [],
    dose_kpi_raw: long ? {
      total_taken_count: dk.total_taken_count,
      total_prescription_count: dk.total_prescription_count,
    } : null,
    weekly_calendar: (long?.recent_records || []).map(r => ({
      date: formatMD(r.date),
      state: RECORD_TO_DOT[r.status] ?? 'empty',
    })),
    message_history: (d.message_history || []).map((m, i) => ({
      id: i,
      sent_at: m.created_at, // formatSentAt이 ISO 처리
      message_type: m.message_type,
      date: m.date,
      title: m.title, // 알림톡 템플릿 이름
      tag_label: m.tag_label, // ⏸ 백엔드 정의 대기 — 없으면 태그 숨김
      is_exclusive: m.is_exclusive,
    })),
  };
};

// ─── Repository 구현 ─────────────────────────────────────────
export class AdherenceRepositoryImpl extends IAdherenceRepository {
  constructor(apiClient) {
    super();
    this.apiClient = apiClient;
    LogUtil.log('🔧 [AdherenceRepository] 초기화');
  }

  async getKpiSummary(_pharmacyBrn) {
    try {
      const res = await this.apiClient.get('/pharm-dashboard/adherence/kpi');
      LogUtil.log('📥 [AdherenceRepository] KPI 응답:', res);
      return ok(res);
    } catch (err) {
      LogUtil.error('❌ [AdherenceRepository] KPI 조회 실패:', err);
      throw new Error(`복약 KPI 조회 실패: ${err.message}`);
    }
  }

  async getPatients(_pharmacyBrn, params = {}) {
    const type = params.type || 'long';
    const path = type === 'long' ? '/pharm-dashboard/dosage/long' : '/pharm-dashboard/dosage/short';
    try {
      const res = await this.apiClient.get(path);
      const data = res?.data || {};
      const mapper = type === 'long' ? mapPatientLong : mapPatientShort;
      const patients = (data.patients || []).map(mapper);
      return {
        result_code: res?.result_code ?? '200',
        resultCode: res?.resultCode ?? 200,
        data: {
          type,
          total: data.summary?.total ?? patients.length,
          patients,
        },
      };
    } catch (err) {
      LogUtil.error('❌ [AdherenceRepository] 환자 리스트 실패:', err);
      throw new Error(`환자 리스트 조회 실패: ${err.message}`);
    }
  }

  async getPatientDetail(userId, type) {
    try {
      const params = { user_id: userId };
      if (type === 'long' || type === 'short') params.type = type;
      const res = await this.apiClient.get('/pharm-dashboard/dosage/detail', { params });
      LogUtil.log('📥 [AdherenceRepository] detail 응답:', res);
      return {
        result_code: res?.result_code ?? '200',
        resultCode: res?.resultCode ?? 200,
        data: mapDetail(res?.data || {}),
      };
    } catch (err) {
      LogUtil.error('❌ [AdherenceRepository] 환자 상세 실패:', err);
      throw new Error(`환자 상세 조회 실패: ${err.message}`);
    }
  }
}
